// @ts-check
/**
 * Typed client for Hermes template drafts (#9552).
 *
 * TYPES ARE GENERATED, NOT HAND-WRITTEN. See `schema.d.ts` header for details.
 *
 *     npm run generate:types-from-openapi-schema
 */
import axios from 'axios';

/** @typedef {import('./schema').components['schemas']} Schemas */
/** @typedef {Schemas['HermesTemplateDraft']} HermesTemplateDraft */
/** @typedef {Schemas['HermesDraftCreate']} HermesDraftCreate */
/** @typedef {Schemas['PatchedHermesTemplateDraft']} PatchedHermesTemplateDraft */
/** @typedef {Schemas['HermesDraftRunRequest']} HermesDraftRunRequest */
/** @typedef {Schemas['HermesDraftPromotePreview']} HermesDraftPromotePreview */
/** @typedef {Schemas['HermesDraftPromoteRequest']} HermesDraftPromoteRequest */
/** @typedef {Schemas['HermesRun']} HermesRun */
/** @typedef {Schemas['HermesTemplate']} HermesTemplate */

const BASE = '/api/hermes/template-drafts';

/**
 * GET /api/hermes/template-drafts/?template= — the drafts of one template.
 * @param {number} templateId
 * @returns {Promise<HermesTemplateDraft[]>}
 */
export async function listDrafts(templateId) {
  const { data } = await axios.get(`${BASE}/`, { params: { template: templateId } });
  return data;
}

/**
 * POST /api/hermes/template-drafts/ — a new draft. Starts from live unless prompt, result_schema or display are given.
 * @param {HermesDraftCreate} body
 * @returns {Promise<HermesTemplateDraft>}
 */
export async function createDraft(body) {
  const { data } = await axios.post(`${BASE}/`, body);
  return data;
}

/**
 * PATCH /api/hermes/template-drafts/{id}/ — save a draft. Never changes the live template.
 * @param {number} id
 * @param {PatchedHermesTemplateDraft} body
 * @returns {Promise<HermesTemplateDraft>}
 */
export async function saveDraft(id, body) {
  const { data } = await axios.patch(`${BASE}/${id}/`, body);
  return data;
}

/**
 * DELETE /api/hermes/template-drafts/{id}/ — discard a draft.
 * @param {number} id
 * @returns {Promise<void>}
 */
export async function discardDraft(id) {
  await axios.delete(`${BASE}/${id}/`);
}

/**
 * POST /api/hermes/template-drafts/{id}/run/ — run the draft on one engagement. Makes no Work or documents.
 * @param {number} id
 * @param {HermesDraftRunRequest} body
 * @returns {Promise<HermesRun>}
 */
export async function runDraft(id, body) {
  const { data } = await axios.post(`${BASE}/${id}/run/`, body);
  return data;
}

/**
 * GET /api/hermes/template-drafts/{id}/promote-preview/ — the version change a promotion would make.
 * @param {number} id
 * @returns {Promise<HermesDraftPromotePreview>}
 */
export async function promotePreview(id) {
  const { data } = await axios.get(`${BASE}/${id}/promote-preview/`);
  return data;
}

/**
 * POST /api/hermes/template-drafts/{id}/promote/ — replace the live template with the draft.
 * @param {number} id
 * @param {HermesDraftPromoteRequest} body
 * @returns {Promise<HermesTemplate>}
 */
export async function promoteDraft(id, body) {
  const { data } = await axios.post(`${BASE}/${id}/promote/`, body);
  return data;
}

/**
 * GET /api/hermes/template-drafts/{id}/runs/ — the draft's own runs, newest first.
 * A nested resource: the draft is in the path, so there are no filters.
 * @param {number} id
 * @returns {Promise<HermesRun[]>}
 */
export async function listDraftRuns(id) {
  const { data } = await axios.get(`${BASE}/${id}/runs/`);
  return data;
}

/**
 * GET /api/hermes/runs/{id}/live/ — the live run to compare a draft run with: the latest completed live run of the
 * same template on the same engagement.
 * @param {number} runId
 * @returns {Promise<HermesRun | null>} null when there is no live run yet (404)
 */
export async function liveRunFor(runId) {
  try {
    const { data } = await axios.get(`/api/hermes/runs/${runId}/live/`);
    return data;
  } catch (e) {
    if (e?.response?.status === 404) return null;
    throw e;
  }
}

/** @typedef {Schemas['TemplateAuthoringRequest']} TemplateAuthoringRequest */
/** @typedef {Schemas['TemplateAuthoringRequestCreate']} TemplateAuthoringRequestCreate */
/** @typedef {Schemas['TemplateAuthoringOutcome']} TemplateAuthoringOutcome */

/**
 * POST /api/hermes/template-drafts/{id}/authoring-requests/ — ask the authoring assistant to change the saved draft.
 * Runs in the background (202); poll with getAuthoringRequest. Never saves the draft.
 * @param {number} id
 * @param {TemplateAuthoringRequestCreate} body
 * @returns {Promise<TemplateAuthoringRequest>}
 */
export async function createAuthoringRequest(id, body) {
  const { data } = await axios.post(`${BASE}/${id}/authoring-requests/`, body);
  return data;
}

/**
 * GET /api/hermes/template-drafts/{id}/authoring-requests/ — the draft's authoring requests, newest first.
 * @param {number} id
 * @returns {Promise<TemplateAuthoringRequest[]>}
 */
export async function listAuthoringRequests(id) {
  const { data } = await axios.get(`${BASE}/${id}/authoring-requests/`);
  return data;
}

/**
 * GET /api/hermes/template-drafts/{id}/authoring-requests/{jobId}/ — one request, for polling.
 * @param {number} id
 * @param {string} jobId
 * @returns {Promise<TemplateAuthoringRequest>}
 */
export async function getAuthoringRequest(id, jobId) {
  const { data } = await axios.get(`${BASE}/${id}/authoring-requests/${jobId}/`);
  return data;
}

/**
 * POST /api/hermes/template-drafts/{id}/authoring-requests/{jobId}/outcome/ — record accepted or rejected.
 * @param {number} id
 * @param {string} jobId
 * @param {TemplateAuthoringOutcome} body
 * @returns {Promise<TemplateAuthoringRequest>}
 */
export async function recordAuthoringOutcome(id, jobId, body) {
  const { data } = await axios.post(`${BASE}/${id}/authoring-requests/${jobId}/outcome/`, body);
  return data;
}
